import { Injectable } from '@angular/core';
import { AngularFirestoreCollection } from '@angular/fire/firestore';

import { combineLatest, Observable, Subject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { ChangeUserPasswordError, CreateUserError, UpdateUserError, UpdateUserMultifactorEnrollmentError } from '../../errors';
import { Role, User } from '../../models';

import { AuthenticationService } from '../authentication.service';
import { RolesService } from '../roles.service';
import { UsersService } from '../users.service';
import { UsersFilters } from '../users.service';

import { FirestoreTimestamp } from './firestore-authentication.service';
import { FirestoreService } from './firestore.service';

interface UserDTO {
    canTakeControlOfEscalatedTickets?: boolean;
    createdAt: FirestoreTimestamp;
    deleted?: boolean;
    email: string;
    id?: string;
    inviteAccepted: boolean;
    isInternal?: boolean;
    lastAccess: FirestoreTimestamp;
    name: string;
    phone?: string;
    photo: string;
    roleId: string;
    tagsIds: string[];
    tenantId: string;
    shouldNotRegisterMetrics?: boolean;
    statusId: string;
}

@Injectable()
export class FirestoreUsersService implements UsersService {
    private get _collection(): AngularFirestoreCollection<UserDTO> {
        return this.firestoreService.collection<UserDTO>('users');
    }

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private rolesService: RolesService
    ) {}

    async updateUserStatus(userId: string, userStatusId: string): Promise<void> {
        await this.firestoreService.cloudFunction('updateUserStatusId')({ userId, userStatusId });
    }

    users(filters?: UsersFilters, includeDeleted?: boolean): Observable<User[]> {
        const subject = new Subject<UserDTO[]>();
        const tenantId = this.authenticationService.user.tenantId;

        let query = this._collection.ref.where('tenantId', '==', tenantId);

        if (!includeDeleted) {
            query = query.where('deleted', '==', false);
        }

        if (filters && filters.rolesIds) {
            query = query.where('roleId', 'in', filters.rolesIds);
        }

        const cancellationFn = query.onSnapshot((snapshot) => {
            const users = snapshot.docs.map<UserDTO>((doc) => {
                const userDTO = doc.data() as UserDTO;
                userDTO.id = doc.id;
                return userDTO;
            });

            subject.next(users);
        });

        return combineLatest([subject.pipe(finalize(() => cancellationFn())), this.rolesService.roles()]).pipe(
            map((res) => {
                const users = res[0];
                const roles = res[1];

                return users.map((user) => this.mapper(roles, user));
            })
        );
    }

    async user(userId: string): Promise<User | undefined> {
        return new Promise<any | undefined>((resolve, reject) => {
            const sub = combineLatest([this._collection.doc(userId).get(), this.rolesService.roles()])
                .pipe(
                    map((res) => {
                        const userDoc = res[0];
                        const roles = res[1];

                        if (!userDoc.exists) {
                            return undefined;
                        }

                        const userDTO = userDoc.data() as UserDTO;
                        userDTO.id = userDoc.id;

                        return this.mapper(roles, userDTO);
                    })
                )
                .subscribe((user) => {
                    resolve(user);
                    sub.unsubscribe();
                });
        });
    }

    async createUser(name: string, email: string, roleId: string, canTakeControlOfEscalatedTickets: boolean, phone?: string, tagsIds?: string[]): Promise<void> {
        const tenantId = this.authenticationService.user.tenantId;
        const createUser = this.firestoreService.cloudFunction('createUser');
        const res = await createUser({ canTakeControlOfEscalatedTickets, name, email, phone, roleId, tagsIds, tenantId });

        if (res && res.data) {
            switch (res.data.code) {
                case 'already-exists':
                    throw new CreateUserError('EMAIL_ALREADY_EXISTS', 'An account with this e-mail already exits.');
                case 'invalid-argument':
                    throw new CreateUserError('INVALID_EMAIL', 'The email has an invalid format.');
                case 'permission-denied':
                    throw new CreateUserError('PERMISSION_DENIED', "The authenticated user don't have permissions to perform this action");
                default:
                    throw new CreateUserError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async updateUser(
        id: string,
        data: { canTakeControlOfEscalatedTickets?: boolean; name?: string; roleId?: string; photo?: string; phone?: string; tagsIds?: string[]; mfaEnabled?: boolean }
    ): Promise<any> {
        const updateUser = this.firestoreService.cloudFunction('Users_UpdateUser');
        const res = await updateUser({ userId: id, ...data });

        if (res && res.data) {
            switch (res.data.code) {
                case 'permission-denied':
                    throw new UpdateUserError('PERMISSION_DENIED', "The authenticated user don't have permissions to perform this action");
                default:
                    throw new CreateUserError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async updateMultipleUsers(
        users: {
            id: string;
            data: { canTakeControlOfEscalatedTickets?: boolean; name?: string; roleId?: string; photo?: string; phone?: string; tagsIds?: string[]; mfaEnabled?: boolean };
        }[]
    ): Promise<any> {
        const updateMultipleUsers = this.firestoreService.cloudFunction('Users_UpdateMultipleUsers');
        const res = await updateMultipleUsers({ users });

        if (res && res.data) {
            switch (res.data.code) {
                case 'permission-denied':
                    throw new UpdateUserError('PERMISSION_DENIED', 'The authenticated user don`t have permissions to perform this action');
                default:
                    throw new CreateUserError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async changeUserPassword(userId: string, currentPassword: string, newPassword: string, newConfirmationPassword: string): Promise<void> {
        // get the user data for the user id to verify if the user exits
        const user = await this.firestoreService.auth.currentUser;
        if (!user) {
            throw new ChangeUserPasswordError('UNKNOWN_USER', 'No user found for the provided userId');
        }

        try {
            await this.firestoreService.auth.signInWithEmailAndPassword(user.email, currentPassword);
        } catch (error) {
            throw new ChangeUserPasswordError('INVALID_PASSWORD', 'The current password is wrong');
        }

        const changePassword = this.firestoreService.cloudFunction('Users_ChangeUserPassword');
        const res = await changePassword({ userId, newPassword, newConfirmationPassword });

        if (res && res.data) {
            const { data } = res;

            switch (data.code) {
                case 'invalid-argument': {
                    switch (data.details) {
                        case 'confirmation-password-dont-match-password':
                            throw new ChangeUserPasswordError(
                                'NEW_AND_CONFIRMATION_PASSWORD_DO_NOT_MATCH',
                                'The new and confirmation password are not equal'
                            );
                        case 'password-insecure':
                            throw new ChangeUserPasswordError(
                                'PASSWORD_INSECURE',
                                "the password don't have the necessary length/chars to be considered secure"
                            );
                        case 'password-already-used':
                            throw new ChangeUserPasswordError('PASSWORD_ALREADY_USED', 'the password has already been used by the user');
                        case 'password-contains-used-data':
                            throw new ChangeUserPasswordError(
                                'PASSWORD_CONTAINS_USER_DATA',
                                'the password contains segments that match some user data (name / email)'
                            );
                    }
                    break;
                }
                case 'permission-denied':
                    throw new ChangeUserPasswordError('PERMISSION_DENIED', 'Only the user can change it own password');
                case 'unknown': // 'unknown' => unknown unexpected error
                    throw new ChangeUserPasswordError('UNKNOWN', 'The current password is invalid');
            }
        }

        if (res && res.data) {
            switch (res.data.code) {
                case 'permission-denied':
                    throw new ChangeUserPasswordError(
                        'PERMISSION_DENIED',
                        "The authenticated user don't have permissions to perform this action"
                    );
                case 'invalid-argument':
                    throw new ChangeUserPasswordError(
                        'INVALID_PASSWORD',
                        'The "newPassword" and "newConfirmationPassword" has different values'
                    );
                default:
                    throw new ChangeUserPasswordError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async deleteUser(id: string): Promise<void> {
        const res = await this.firestoreService.cloudFunction('deleteUser')({ userId: id });

        if (res && res.data) {
            switch (res.data.code) {
                case 'permission-denied':
                    throw new UpdateUserError('PERMISSION_DENIED', "The authenticated user don't have permissions to perform this action");
                default:
                    throw new CreateUserError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    async resetPasswordRequest(email: string): Promise<void> {
        try {
            await this.authenticationService.resetPasswordRequest(email);
        } catch (error) {
            throw error;
        }
    }

    async resendConfirmationEmail(email: string): Promise<void> {
        try {
            await this.authenticationService.resendConfirmationEmail(email);
        } catch (error) {
            throw error;
        }
    }

    async updateMultifactorAuthenticationToTenantsUsers(tenantId: string, action: 'add' | 'remove'): Promise<any> {
        const updateMultifactorTenantUsers = this.firestoreService.cloudFunction('updateMultifactorAuthenticationToTenantsUsers');
        const res = await updateMultifactorTenantUsers({ tenantId, action });

        if (res && res.data) {
            switch (res.data.code) {
                case 'failed-precondition':
                    throw new UpdateUserMultifactorEnrollmentError('FAILED-ENROLLMENT', 'Could not update multifactor enrollment');
                default:
                    throw new CreateUserError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    private mapper(roles: Role[], source: UserDTO): User {
        return {
            canTakeControlOfEscalatedTickets: source.canTakeControlOfEscalatedTickets ?? false,
            createdAt: source.createdAt.toDate(),
            deleted: source.deleted ?? false,
            email: source.email,
            id: source.id,
            inviteAccepted: source.inviteAccepted,
            isInternal: source.isInternal ?? false,
            lastAccess: source.lastAccess ? source.lastAccess.toDate() : null,
            name: source.name,
            phone: source?.phone,
            photo: source.photo,
            role: roles.find((r) => r.id === source.roleId),
            tagsIds: source.tagsIds,
            tenantId: source.tenantId,
            shouldNotRegisterMetrics: source?.shouldNotRegisterMetrics ?? false,
            statusId: source?.statusId ? source.statusId : environment.operatorUnavailableStatuses,
        };
    }
}
